
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FilePlus, 
  Search, 
  Eye, 
  Edit, 
  Calendar, 
  User, 
  Filter
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PatientEmptyState from '@/components/doctor/PatientEmptyState';
import { formatDistance } from 'date-fns';
import PatientSelectorModal from '@/components/doctor/PatientSelectorModal';

const mockTreatmentPlans = [
  {
    id: '1',
    patientName: 'John Doe',
    patientId: '101',
    treatmentType: 'Dental Implant',
    status: 'active',
    createdAt: new Date(2025, 2, 15),
    lastUpdated: new Date(2025, 3, 10),
    canEdit: true
  },
  {
    id: '2',
    patientName: 'Jane Smith',
    patientId: '102',
    treatmentType: 'Orthodontics',
    status: 'pending',
    createdAt: new Date(2025, 3, 5),
    lastUpdated: new Date(2025, 3, 5),
    canEdit: true
  },
  {
    id: '3',
    patientName: 'Robert Johnson',
    patientId: '103',
    treatmentType: 'Root Canal',
    status: 'completed',
    createdAt: new Date(2025, 2, 1),
    lastUpdated: new Date(2025, 3, 1),
    canEdit: false
  }
];

const MyTreatmentPlansPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [plans, setPlans] = useState(mockTreatmentPlans);
  const [isPatientSelectorOpen, setIsPatientSelectorOpen] = useState(false);
  const navigate = useNavigate();

  const filteredPlans = plans.filter(plan => 
    plan.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.treatmentType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getBadgeVariant = (status: string) => {
    switch(status) {
      case 'active':
        return 'default';
      case 'pending':
        return 'outline';
      case 'completed':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const handlePatientSelect = (patientId: string) => {
    navigate(`/create-treatment-plan/${patientId}`);
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold">My Treatment Plans</h1>
        <Button onClick={() => setIsPatientSelectorOpen(true)}>
          <FilePlus className="mr-2 h-4 w-4" />
          Create New Plan
        </Button>
      </div>

      <Card className="mb-8">
        <CardHeader className="pb-2">
          <CardTitle>Treatment Plans</CardTitle>
          <CardDescription>
            View and manage all your patient treatment plans
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search" 
                placeholder="Search by patient or treatment..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
              <span className="sr-only">Filter</span>
            </Button>
          </div>

          {filteredPlans.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient Name</TableHead>
                  <TableHead>Treatment Summary</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <Link to={`/case-file/${plan.patientId}`} className="text-primary hover:underline">
                          {plan.patientName}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell>{plan.treatmentType}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDistance(plan.createdAt, new Date(), { addSuffix: true })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getBadgeVariant(plan.status)}>
                        {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/case-file/${plan.patientId}`}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Link>
                        </Button>
                        {plan.canEdit && (
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/create-treatment-plan/${plan.patientId}`}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Link>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8">
              <PatientEmptyState />
            </div>
          )}
        </CardContent>
      </Card>

      <PatientSelectorModal
        open={isPatientSelectorOpen}
        onClose={() => setIsPatientSelectorOpen(false)}
        onPatientSelect={handlePatientSelect}
      />
    </div>
  );
};

export default MyTreatmentPlansPage;
